import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { attendanceApi, teachersApi, staffApi, salariesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, Money, CheckCircle, XCircle, Clock, PencilSimple, Trash, Bank, Hourglass } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const Attendance = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [attendance, setAttendance] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [canteenStaff, setCanteenStaff] = useState([]);
  const [transportStaff, setTransportStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [salaryDialogOpen, setSalaryDialogOpen] = useState(false);
  const [editingSalaryId, setEditingSalaryId] = useState(null);
  const [deleteSalaryTarget, setDeleteSalaryTarget] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedStaffType, setSelectedStaffType] = useState('teacher');

  const [attendanceForm, setAttendanceForm] = useState({
    staff_id: '',
    staff_type: 'teacher',
    date: new Date().toISOString().slice(0, 10),
    status: 'present'
  });

  const initialSalaryForm = {
    staff_id: '',
    staff_type: 'teacher',
    amount: '',
    month: new Date().toLocaleString('fr-FR', { month: 'long' }),
    year: new Date().getFullYear(),
    payment_method: 'transfer',
    status: 'completed',
    transfer_reference: '',
    transfer_date: '',
    notes: '',
  };
  const [salaryForm, setSalaryForm] = useState(initialSalaryForm);

  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const paymentMethods = [
    { value: 'cash', label: t('cash') },
    { value: 'cheque', label: t('cheque') },
    { value: 'transfer', label: t('transfer') },
    { value: 'deposit', label: t('deposit') }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const [educators, setEducators] = useState([]);
  const [cleaningStaff, setCleaningStaff] = useState([]);

  const loadData = async () => {
    try {
      const [attendanceRes, salariesRes, teachersRes, canteenRes, transportRes, educatorsRes, cleaningRes] = await Promise.all([
        attendanceApi.getAll(),
        salariesApi.getAll(),
        teachersApi.getAll(),
        staffApi.getAll({ staff_type: 'canteen' }),
        staffApi.getAll({ staff_type: 'transport' }),
        staffApi.getAll({ staff_type: 'educator' }),
        staffApi.getAll({ staff_type: 'cleaning' }),
      ]);
      setAttendance(attendanceRes.data);
      setSalaries(salariesRes.data);
      setTeachers(teachersRes.data);
      setCanteenStaff(canteenRes.data);
      setTransportStaff(transportRes.data);
      setEducators(educatorsRes.data);
      setCleaningStaff(cleaningRes.data);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAttendanceSubmit = async (e) => {
    e.preventDefault();
    try {
      await attendanceApi.create(attendanceForm);
      toast.success(t('success'));
      setAttendanceDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleSalarySubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...salaryForm,
        amount: parseFloat(salaryForm.amount),
      };
      // Strip empty optional fields
      ['transfer_reference', 'transfer_date', 'notes'].forEach(k => {
        if (!payload[k]) delete payload[k];
      });
      if (editingSalaryId) {
        await salariesApi.update(editingSalaryId, payload);
        toast.success(language === 'fr' ? 'Versement mis à jour' : 'Salary updated');
      } else {
        await salariesApi.create(payload);
        toast.success(t('success'));
      }
      setSalaryDialogOpen(false);
      setEditingSalaryId(null);
      setSalaryForm(initialSalaryForm);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const openEditSalary = (s) => {
    setEditingSalaryId(s.id);
    setSalaryForm({
      staff_id: s.staff_id || '',
      staff_type: s.staff_type || 'teacher',
      amount: s.amount || '',
      month: s.month || '',
      year: s.year || new Date().getFullYear(),
      payment_method: s.payment_method || 'transfer',
      status: s.status || 'completed',
      transfer_reference: s.transfer_reference || '',
      transfer_date: s.transfer_date || '',
      notes: s.notes || '',
    });
    setSalaryDialogOpen(true);
  };

  const confirmDeleteSalary = async () => {
    if (!deleteSalaryTarget) return;
    try {
      await salariesApi.delete(deleteSalaryTarget.id);
      toast.success(
        language === 'fr'
          ? `Versement supprimé (annulable 7j via /admin/bulk-operations)`
          : `Salary deleted (undo available 7d via /admin/bulk-operations)`
      );
      setDeleteSalaryTarget(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const getStaffList = (type) => {
    switch(type) {
      case 'teacher': return teachers;
      case 'canteen': return canteenStaff;
      case 'transport': return transportStaff;
      case 'educator': return educators;
      case 'cleaning': return cleaningStaff;
      default: return [];
    }
  };

  const staffTypeLabel = (type) => {
    switch (type) {
      case 'teacher': return t('teachers');
      case 'canteen': return t('canteenStaff');
      case 'transport': return t('transportStaff');
      case 'educator': return t('educators');
      case 'cleaning': return t('cleaningStaff');
      default: return type;
    }
  };

  const staffTypeBadge = (type) => {
    switch (type) {
      case 'teacher': return 'bg-blue-100 text-blue-700';
      case 'educator': return 'bg-indigo-100 text-indigo-700';
      case 'canteen': return 'bg-green-100 text-green-700';
      case 'transport': return 'bg-yellow-100 text-yellow-700';
      case 'cleaning': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'present': return <CheckCircle size={18} className="text-[#10B981]" weight="fill" />;
      case 'absent': return <XCircle size={18} className="text-[#EF4444]" weight="fill" />;
      case 'late': return <Clock size={18} className="text-[#F59E0B]" weight="fill" />;
      default: return null;
    }
  };

  const filteredAttendance = attendance.filter(a => 
    a.staff_type === selectedStaffType && a.date === selectedDate
  );

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">{t('attendance')} & {t('salaries')}</h1>

      <Tabs defaultValue="attendance" className="space-y-6">
        <TabsList className="bg-[#F1F5F9]">
          <TabsTrigger value="attendance" className="data-[state=active]:bg-white" data-testid="tab-attendance">
            <Calendar size={18} className="mr-2" />
            {t('attendance')}
          </TabsTrigger>
          <TabsTrigger value="salaries" className="data-[state=active]:bg-white" data-testid="tab-salaries">
            <Money size={18} className="mr-2" />
            {t('salaries')}
          </TabsTrigger>
        </TabsList>

        {/* Attendance Tab */}
        <TabsContent value="attendance" className="space-y-4">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="space-y-2">
                  <Label className="font-manrope">{t('date')}</Label>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="border-[#E2E8F0] w-[180px]"
                    data-testid="attendance-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-manrope">{language === 'fr' ? 'Type de personnel' : 'Staff Type'}</Label>
                  <Select value={selectedStaffType} onValueChange={setSelectedStaffType}>
                    <SelectTrigger className="w-[180px] border-[#E2E8F0]" data-testid="attendance-staff-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="teacher">{t('teachers')}</SelectItem>
                      <SelectItem value="educator">{t('educators')}</SelectItem>
                      <SelectItem value="canteen">{t('canteenStaff')}</SelectItem>
                      <SelectItem value="transport">{t('transportStaff')}</SelectItem>
                      <SelectItem value="cleaning">{t('cleaningStaff')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={() => {
                    setAttendanceForm({ ...attendanceForm, staff_type: selectedStaffType, date: selectedDate });
                    setAttendanceDialogOpen(true);
                  }}
                  className="bg-[#0052FF] hover:bg-[#0043D1]"
                  data-testid="mark-attendance-btn"
                >
                  {t('markAttendance')}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0]">
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{language === 'fr' ? 'Nom' : 'Name'}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('date')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAttendance.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-[#64748B]">{t('noData')}</TableCell>
                    </TableRow>
                  ) : (
                    filteredAttendance.map((a) => (
                      <TableRow key={a.id} className="border-[#E2E8F0] hover:bg-[#F1F5F9]">
                        <TableCell className="font-manrope">{a.staff_name}</TableCell>
                        <TableCell className="font-manrope">{a.date}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(a.status)}
                            <span className="font-manrope">{t(a.status)}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Salaries Tab */}
        <TabsContent value="salaries" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => { setEditingSalaryId(null); setSalaryForm(initialSalaryForm); setSalaryDialogOpen(true); }}
              className="bg-[#0052FF] hover:bg-[#0043D1]"
              data-testid="add-salary-btn"
            >
              <Money size={18} className="mr-2" />
              {language === 'fr' ? 'Payer salaire' : 'Pay Salary'}
            </Button>
          </div>

          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0]">
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{language === 'fr' ? 'Nom' : 'Name'}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{language === 'fr' ? 'Type' : 'Type'}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{language === 'fr' ? 'Mois' : 'Month'}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('paymentMethod')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{language === 'fr' ? 'Réf. virement' : 'Transfer ref'}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A] text-right">{t('amount')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A] text-right">{language === 'fr' ? 'Actions' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salaries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-[#64748B]">{t('noData')}</TableCell>
                    </TableRow>
                  ) : (
                    salaries.map((salary) => (
                      <TableRow key={salary.id} className="border-[#E2E8F0] hover:bg-[#F1F5F9]" data-testid={`salary-row-${salary.id}`}>
                        <TableCell className="font-manrope">{salary.staff_name}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${staffTypeBadge(salary.staff_type)}`}>
                            {staffTypeLabel(salary.staff_type)}
                          </span>
                        </TableCell>
                        <TableCell className="font-manrope">{salary.month} {salary.year}</TableCell>
                        <TableCell className="font-manrope">
                          <span className="inline-flex items-center gap-1">
                            {salary.payment_method === 'transfer' && <Bank size={13} className="text-indigo-600" />}
                            {t(salary.payment_method)}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-indigo-700">{salary.transfer_reference || '—'}</TableCell>
                        <TableCell>
                          {salary.status === 'pending' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                              <Hourglass size={12} /> {language === 'fr' ? 'En attente' : 'Pending'}
                            </span>
                          ) : salary.status === 'failed' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                              <XCircle size={12} /> {language === 'fr' ? 'Échec' : 'Failed'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">
                              <CheckCircle size={12} /> {language === 'fr' ? 'Effectué' : 'Done'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-manrope font-semibold text-right">{formatCurrency(salary.amount)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditSalary(salary)}
                            className="hover:bg-[#EFF6FF] text-[#0052FF]"
                            data-testid={`edit-salary-${salary.id}`}
                            title={language === 'fr' ? 'Modifier' : 'Edit'}
                          >
                            <PencilSimple size={16} />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteSalaryTarget(salary)}
                              className="hover:bg-red-50 text-red-600"
                              data-testid={`delete-salary-${salary.id}`}
                              title={language === 'fr' ? 'Supprimer' : 'Delete'}
                            >
                              <Trash size={16} />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Attendance Dialog */}
      <Dialog open={attendanceDialogOpen} onOpenChange={setAttendanceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">{t('markAttendance')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAttendanceSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="font-manrope">{language === 'fr' ? 'Personnel' : 'Staff'}</Label>
              <Select 
                value={attendanceForm.staff_id} 
                onValueChange={(v) => setAttendanceForm({ ...attendanceForm, staff_id: v })}
              >
                <SelectTrigger className="border-[#E2E8F0]" data-testid="attendance-staff">
                  <SelectValue placeholder={language === 'fr' ? 'Sélectionner' : 'Select'} />
                </SelectTrigger>
                <SelectContent>
                  {getStaffList(attendanceForm.staff_type).map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.first_name} {s.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{language === 'fr' ? 'Statut' : 'Status'}</Label>
              <Select 
                value={attendanceForm.status} 
                onValueChange={(v) => setAttendanceForm({ ...attendanceForm, status: v })}
              >
                <SelectTrigger className="border-[#E2E8F0]" data-testid="attendance-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">{t('present')}</SelectItem>
                  <SelectItem value="absent">{t('absent')}</SelectItem>
                  <SelectItem value="late">{t('late')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAttendanceDialogOpen(false)}>{t('cancel')}</Button>
              <Button type="submit" className="bg-[#0052FF] hover:bg-[#0043D1]" data-testid="save-attendance-btn">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Salary Dialog */}
      <Dialog open={salaryDialogOpen} onOpenChange={(o) => { setSalaryDialogOpen(o); if (!o) setEditingSalaryId(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">
              {editingSalaryId
                ? (language === 'fr' ? 'Modifier le versement' : 'Edit salary')
                : (language === 'fr' ? 'Payer salaire' : 'Pay Salary')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSalarySubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="font-manrope">{language === 'fr' ? 'Type de personnel' : 'Staff Type'}</Label>
              <Select 
                value={salaryForm.staff_type} 
                onValueChange={(v) => setSalaryForm({ ...salaryForm, staff_type: v, staff_id: '' })}
              >
                <SelectTrigger className="border-[#E2E8F0]" data-testid="salary-staff-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="teacher">{t('teachers')}</SelectItem>
                  <SelectItem value="educator">{t('educators')}</SelectItem>
                  <SelectItem value="canteen">{t('canteenStaff')}</SelectItem>
                  <SelectItem value="transport">{t('transportStaff')}</SelectItem>
                  <SelectItem value="cleaning">{t('cleaningStaff')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{language === 'fr' ? 'Personnel' : 'Staff'}</Label>
              <Select 
                value={salaryForm.staff_id} 
                onValueChange={(v) => setSalaryForm({ ...salaryForm, staff_id: v })}
              >
                <SelectTrigger className="border-[#E2E8F0]" data-testid="salary-staff">
                  <SelectValue placeholder={language === 'fr' ? 'Sélectionner' : 'Select'} />
                </SelectTrigger>
                <SelectContent>
                  {getStaffList(salaryForm.staff_type).map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.first_name} {s.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-manrope">{language === 'fr' ? 'Mois' : 'Month'}</Label>
                <Select 
                  value={salaryForm.month} 
                  onValueChange={(v) => setSalaryForm({ ...salaryForm, month: v })}
                >
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="salary-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-manrope">{language === 'fr' ? 'Année' : 'Year'}</Label>
                <Input
                  type="number"
                  value={salaryForm.year}
                  onChange={(e) => setSalaryForm({ ...salaryForm, year: parseInt(e.target.value) })}
                  className="border-[#E2E8F0]"
                  data-testid="salary-year"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('amount')} (FCFA)</Label>
              <Input
                type="number"
                value={salaryForm.amount}
                onChange={(e) => setSalaryForm({ ...salaryForm, amount: e.target.value })}
                required
                className="border-[#E2E8F0]"
                data-testid="salary-amount"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('paymentMethod')}</Label>
              <Select 
                value={salaryForm.payment_method} 
                onValueChange={(v) => setSalaryForm({ ...salaryForm, payment_method: v })}
              >
                <SelectTrigger className="border-[#E2E8F0]" data-testid="salary-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map(pm => (
                    <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {salaryForm.payment_method === 'transfer' && (
              <div className="space-y-2 rounded-md border border-indigo-100 bg-indigo-50/30 p-3">
                <Label className="font-manrope text-xs text-indigo-700 font-semibold">
                  <Bank size={12} className="inline mr-1" />
                  {language === 'fr' ? 'Détails du virement' : 'Transfer details'}
                </Label>
                <Input
                  type="text"
                  placeholder={language === 'fr' ? 'Référence (ex: VIR-2026-001)' : 'Reference (ex: WT-2026-001)'}
                  value={salaryForm.transfer_reference}
                  onChange={(e) => setSalaryForm({ ...salaryForm, transfer_reference: e.target.value })}
                  className="border-[#E2E8F0]"
                  data-testid="salary-transfer-ref"
                />
                <Input
                  type="date"
                  placeholder={language === 'fr' ? "Date d'exécution" : 'Execution date'}
                  value={salaryForm.transfer_date}
                  onChange={(e) => setSalaryForm({ ...salaryForm, transfer_date: e.target.value })}
                  className="border-[#E2E8F0]"
                  data-testid="salary-transfer-date"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label className="font-manrope">{language === 'fr' ? 'Statut' : 'Status'}</Label>
              <Select
                value={salaryForm.status}
                onValueChange={(v) => setSalaryForm({ ...salaryForm, status: v })}
              >
                <SelectTrigger className="border-[#E2E8F0]" data-testid="salary-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="completed">{language === 'fr' ? 'Effectué' : 'Completed'}</SelectItem>
                  <SelectItem value="pending">{language === 'fr' ? 'En attente' : 'Pending'}</SelectItem>
                  <SelectItem value="failed">{language === 'fr' ? 'Échec' : 'Failed'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="font-manrope">{language === 'fr' ? 'Notes (facultatif)' : 'Notes (optional)'}</Label>
              <Input
                type="text"
                value={salaryForm.notes}
                onChange={(e) => setSalaryForm({ ...salaryForm, notes: e.target.value })}
                className="border-[#E2E8F0]"
                data-testid="salary-notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setSalaryDialogOpen(false); setEditingSalaryId(null); }}>{t('cancel')}</Button>
              <Button type="submit" className="bg-[#0052FF] hover:bg-[#0043D1]" data-testid="save-salary-btn">{editingSalaryId ? (language === 'fr' ? 'Mettre à jour' : 'Update') : t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete salary confirmation (admin only) */}
      <Dialog open={!!deleteSalaryTarget} onOpenChange={(o) => { if (!o) setDeleteSalaryTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="delete-salary-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl text-red-700 flex items-center gap-2">
              <Trash size={20} />
              {language === 'fr' ? 'Supprimer ce versement ?' : 'Delete this salary?'}
            </DialogTitle>
          </DialogHeader>
          {deleteSalaryTarget && (
            <div className="space-y-2 font-manrope text-sm">
              <div><strong>{language === 'fr' ? 'Bénéficiaire' : 'Beneficiary'}:</strong> {deleteSalaryTarget.staff_name}</div>
              <div><strong>{language === 'fr' ? 'Mois' : 'Month'}:</strong> {deleteSalaryTarget.month} {deleteSalaryTarget.year}</div>
              <div><strong>{language === 'fr' ? 'Montant' : 'Amount'}:</strong> {formatCurrency(deleteSalaryTarget.amount)}</div>
              {deleteSalaryTarget.transfer_reference && (
                <div><strong>{language === 'fr' ? 'Référence' : 'Reference'}:</strong> <span className="font-mono">{deleteSalaryTarget.transfer_reference}</span></div>
              )}
              <p className="text-red-600 text-xs pt-2">
                {language === 'fr'
                  ? '⚠️ Annulable pendant 7 jours via /admin/bulk-operations.'
                  : '⚠️ Undoable within 7 days via /admin/bulk-operations.'}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteSalaryTarget(null)} data-testid="salary-delete-cancel">{t('cancel')}</Button>
            <Button onClick={confirmDeleteSalary} className="bg-red-600 hover:bg-red-700 text-white" data-testid="salary-delete-confirm">
              {language === 'fr' ? 'Supprimer' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Attendance;
